/**
 * /unete?code=XXXXXX — Landing de invitación de membresía con checkout integrado.
 *
 * Flujo:
 *   1. Valida el code contra /api/invitation-codes/validate
 *   2. Si expirado → pantalla de "Invitación vencida"
 *   3. Si válido → muestra countdown + CTA de checkout
 *   4. Checkout → Stripe → /registro?from_basic=1&plan_tier=basic → /pending-approval
 */

import { getApiBaseUrl } from "@/config/apiUrl";
import { BRAND_PERSON_NAME } from "@/lib/brandConfig";
import { HEADER_PROFILE_IMAGE_URL } from "@/config/cloudflare";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AlertTriangle, Clock, Loader2, Lock, ShieldCheck, Sparkles } from "lucide-react";
import { flushPostHogBeforeNavigation } from "@/lib/posthogAnalytics";

interface ValidateResponse {
  valid: boolean;
  message?: string;
  valid_for_seconds?: number;
  valid_until_iso?: string;
  checkout_plan_tier?: string;
  checkout_product_id?: string;
  checkout_amount_cents?: number;
  checkout_currency?: string;
  checkout_product_name?: string;
  checkout_payment_provider?: string;
  checkout_discount_percentage?: number;
  layout?: {
    title?: string;
    subtitle?: string;
    message?: string;
    image_url?: string;
    background_image_url?: string;
  };
}

function formatCountdown(seconds: number): { mm: string; ss: string } {
  const s = Math.max(0, seconds);
  const mm = String(Math.floor(s / 60)).padStart(2, "0");
  const ss = String(s % 60).padStart(2, "0");
  return { mm, ss };
}

export default function InviteMembership() {
  const [params] = useSearchParams();
  const code = (params.get("code") || "").trim().toUpperCase();
  const fromStripe = params.get("from_stripe") === "1";

  const [status, setStatus] = useState<"loading" | "valid" | "expired" | "error">("loading");
  const [data, setData] = useState<ValidateResponse | null>(null);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);

  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Validate on mount
  useEffect(() => {
    if (!code) {
      setStatus("expired");
      return;
    }
    const apiBase = getApiBaseUrl();
    fetch(`${apiBase}/api/invitation-codes/validate?code=${encodeURIComponent(code)}`)
      .then((r) => r.json())
      .then((json: ValidateResponse) => {
        if (!json.valid) {
          setStatus("expired");
          return;
        }
        setData(json);
        setSecondsLeft(json.valid_for_seconds ?? 0);
        setStatus("valid");
      })
      .catch(() => setStatus("error"));
  }, [code]);

  // Countdown tick
  useEffect(() => {
    if (status !== "valid") return;
    tickRef.current = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          clearInterval(tickRef.current!);
          setStatus("expired");
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => { if (tickRef.current) clearInterval(tickRef.current); };
  }, [status]);

  const handleCheckout = async () => {
    if (!data || checkoutLoading) return;
    setCheckoutError(null);
    setCheckoutLoading(true);

    const amountCents = data.checkout_amount_cents ?? 699;
    const currency = (data.checkout_currency ?? "usd").toLowerCase();
    const planTier = data.checkout_plan_tier ?? "basic";
    const lcPlan = planTier.toLowerCase();
    const productName = data.checkout_product_name ?? `Membresía ${BRAND_PERSON_NAME}`;
    const provider = (data.checkout_payment_provider ?? "stripe").toLowerCase();
    const discountPct = data.checkout_discount_percentage ?? 0;
    const origin = window.location.origin;

    // Build MP success path with membership tier params for post-payment messaging in /registro
    const mpFromParam = lcPlan === "vip" || lcPlan === "platinum" || lcPlan === "devil"
      ? "from_vip=1"
      : lcPlan === "standard"
      ? "from_standard=1"
      : "from_basic=1";
    const mpSuccessPath = `/portal/registro?${mpFromParam}&plan_tier=${encodeURIComponent(planTier)}&from_mp=true`;

    try {
      if (provider === "mercadopago") {
        const res = await fetch("/api/mercadopago/create-preference", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            productName,
            price: amountCents / 100,
            currency: currency.toUpperCase(),
            successPath: mpSuccessPath,
            failurePath: `/unete?code=${code}`,
            pendingPath: `/unete?code=${code}`,
            metadata: {
              plan_tier: planTier,
              checkout_type: "membership_invite",
              invite_code: code,
            },
          }),
        });
        const json = await res.json().catch(() => ({})) as {
          init_point?: string;
          sandbox_init_point?: string;
          error?: string;
        };
        const mpUrl = json.init_point ?? json.sandbox_init_point;
        if (!res.ok || !mpUrl) {
          setCheckoutError(json.error ?? "No se pudo iniciar el pago con MercadoPago. Intentá de nuevo.");
          return;
        }
        window.location.assign(mpUrl);
      } else {
        const isStripePriceId = data.checkout_product_id?.startsWith("price_");

        await flushPostHogBeforeNavigation(350);

        const res = await fetch("/api/create-subscription-checkout", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            amount: amountCents,
            currency,
            productName,
            ...(isStripePriceId ? { stripe_price_id: data.checkout_product_id } : {}),
            ...(discountPct > 0 ? { discountPercentage: discountPct, originalAmount: amountCents } : {}),
            successUrl: `${origin}/registro`,
            cancelUrl: `${origin}/unete?code=${code}&from_stripe=1`,
            metadata: {
              plan_tier: planTier,
              checkout_type: "membership_invite",
              invite_code: code,
              ...(data.checkout_product_id && !isStripePriceId ? { stripe_product_id: data.checkout_product_id } : {}),
            },
          }),
        });
        const json = await res.json().catch(() => ({})) as { url?: string; error?: string };
        if (!res.ok || !json.url) {
          setCheckoutError(json.error ?? "No se pudo iniciar el pago. Intentá de nuevo.");
          return;
        }
        window.location.assign(json.url);
      }
    } catch {
      setCheckoutError("Error de conexión. Intentá de nuevo.");
    } finally {
      setCheckoutLoading(false);
    }
  };

  const discountPctDisplay = data?.checkout_discount_percentage ?? 0;
  const originalAmountCents = data?.checkout_amount_cents ?? null;
  const discountedAmountCents =
    originalAmountCents != null && discountPctDisplay > 0
      ? Math.round(originalAmountCents * (1 - discountPctDisplay / 100))
      : originalAmountCents;
  const currencyLabel = (data?.checkout_currency ?? "USD").toUpperCase();
  const amountDisplay =
    discountedAmountCents != null
      ? `$${(discountedAmountCents / 100).toFixed(2)} ${currencyLabel}`
      : null;
  const originalAmountDisplay =
    originalAmountCents != null && discountPctDisplay > 0
      ? `$${(originalAmountCents / 100).toFixed(2)} ${currencyLabel}`
      : null;

  const { mm, ss } = formatCountdown(secondsLeft);
  const bg = data?.layout?.background_image_url;
  const logo = data?.layout?.image_url ?? HEADER_PROFILE_IMAGE_URL;

  // ── Loading ──────────────────────────────────────────────────────────────────
  if (status === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-950">
        <Loader2 className="h-8 w-8 animate-spin text-zinc-500" />
      </div>
    );
  }

  // ── Expired / Error ───────────────────────────────────────────────────────────
  if (status === "expired" || status === "error") {
    const expiredTitle = fromStripe ? "El link venció durante el pago" : "Invitación vencida";
    const expiredMsg = fromStripe
      ? "Volviste del checkout de Stripe, pero el link ya expiró. Pedile al equipo un nuevo link de acceso."
      : "Esta invitación ya no está disponible. Pedile al equipo un nuevo link de acceso.";
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-950 px-4">
        <div className="max-w-sm w-full space-y-6 text-center">
          <div className="flex justify-center">
            <div className="rounded-full bg-zinc-800 p-4">
              <Clock className="h-8 w-8 text-zinc-500" />
            </div>
          </div>
          <div className="space-y-2">
            <h1 className="text-xl font-semibold text-white">
              {status === "error" ? "No se pudo verificar" : expiredTitle}
            </h1>
            <p className="text-sm text-zinc-400">
              {status === "error"
                ? "Hubo un problema al verificar tu invitación. Solicitá un nuevo link."
                : expiredMsg}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ── Valid ─────────────────────────────────────────────────────────────────────
  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center px-4 py-12 bg-zinc-950 relative"
      style={bg ? { backgroundImage: `url(${bg})`, backgroundSize: "cover", backgroundPosition: "center" } : undefined}
    >
      {/* Overlay */}
      {bg && <div className="absolute inset-0 bg-zinc-950/70 backdrop-blur-sm" />}

      <div className="relative z-10 w-full max-w-sm space-y-6">
        {/* Avatar + nombre */}
        <div className="flex flex-col items-center gap-3">
          {logo && (
            <img
              src={logo}
              alt={BRAND_PERSON_NAME}
              className="h-16 w-16 rounded-full object-cover ring-2 ring-white/20"
            />
          )}
          <p className="text-sm font-medium text-zinc-300 tracking-wide uppercase">
            {BRAND_PERSON_NAME}
          </p>
        </div>

        {/* Título */}
        <div className="text-center space-y-2">
          <div className="flex justify-center">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-500/40 bg-amber-500/10 px-3 py-1 text-xs font-medium text-amber-300">
              <Sparkles className="h-3 w-3" />
              Invitación exclusiva
            </span>
          </div>
          <h1 className="text-2xl font-bold text-white leading-tight">
            {data?.layout?.title ?? "Acceso a la membresía privada"}
          </h1>
          {data?.layout?.subtitle && (
            <p className="text-sm text-zinc-400">{data.layout.subtitle}</p>
          )}
        </div>

        {/* Countdown */}
        <div className="rounded-2xl border border-white/10 bg-white/5 p-5 text-center space-y-1 backdrop-blur-sm">
          <p className="text-xs text-zinc-400 uppercase tracking-widest">Esta invitación vence en</p>
          <div className="flex items-center justify-center gap-1 tabular-nums">
            <span className="text-5xl font-bold text-white">{mm}</span>
            <span className="text-3xl font-bold text-zinc-500 mb-1">:</span>
            <span className="text-5xl font-bold text-white">{ss}</span>
          </div>
          <p className="text-xs text-zinc-500">min : seg</p>
        </div>

        {/* Card de oferta */}
        <div className="rounded-2xl border border-white/10 bg-white/5 p-5 space-y-3 backdrop-blur-sm">
          {data?.layout?.message ? (
            <p className="text-sm text-zinc-300 text-center">{data.layout.message}</p>
          ) : (
            <p className="text-sm text-zinc-300 text-center">
              Accedé al grupo privado de {BRAND_PERSON_NAME} con contenido exclusivo, actualizaciones y comunidad.
            </p>
          )}

          {amountDisplay && (
            <div className="text-center">
              {originalAmountDisplay && (
                <span className="mr-2 text-base text-zinc-500 line-through">{originalAmountDisplay}</span>
              )}
              <span className="text-3xl font-bold text-white">{amountDisplay}</span>
              <span className="ml-1 text-sm text-zinc-400">/mes</span>
              {discountPctDisplay > 0 && (
                <span className="ml-2 inline-flex items-center rounded-full bg-emerald-500/20 border border-emerald-500/30 px-2 py-0.5 text-xs font-medium text-emerald-300">
                  -{discountPctDisplay}%
                </span>
              )}
            </div>
          )}

          <ul className="space-y-1.5 text-sm text-zinc-300">
            <li className="flex items-center gap-2"><ShieldCheck className="h-3.5 w-3.5 text-emerald-400 shrink-0" /> Cancelá cuando quieras</li>
            <li className="flex items-center gap-2"><Lock className="h-3.5 w-3.5 text-emerald-400 shrink-0" /> Acceso privado e inmediato</li>
            <li className="flex items-center gap-2"><Sparkles className="h-3.5 w-3.5 text-amber-400 shrink-0" /> Contenido exclusivo mensual</li>
          </ul>
        </div>

        {/* CTA */}
        <Button
          onClick={handleCheckout}
          disabled={checkoutLoading || secondsLeft <= 0}
          className="w-full h-12 text-base font-semibold bg-white text-zinc-950 hover:bg-zinc-100 rounded-xl"
        >
          {checkoutLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Preparando pago…
            </>
          ) : (
            <>Unirme ahora{amountDisplay ? ` — ${amountDisplay}/mes` : ""}</>
          )}
        </Button>

        {checkoutError && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{checkoutError}</AlertDescription>
          </Alert>
        )}

        <p className="text-center text-xs text-zinc-600">
          Al continuar aceptás los términos de la membresía. Podés cancelar en cualquier momento.
        </p>
      </div>
    </div>
  );
}
